/**
 * PostHog analytics shim — single import surface for the app.
 *
 * Why a wrapper instead of raw posthog.capture() calls scattered across
 * components? Three reasons:
 *   1. Enforces our event taxonomy (string typos won't silently create new
 *      events in PostHog).
 *   2. Lets us no-op cleanly when REACT_APP_POSTHOG_KEY isn't set (preview,
 *      tests, contributor forks).
 *   3. Single place to add cross-cutting properties (environment, app version)
 *      without touching every call site.
 */
import posthog from "posthog-js";

const KEY = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

let initialized = false;

export function initAnalytics() {
  if (initialized || !KEY) return;
  posthog.init(KEY, {
    api_host: HOST,
    autocapture: true,
    capture_pageview: false, // we fire $pageview manually on route change
    disable_session_recording: true,
    persistence: "localStorage+cookie",
    loaded: (ph) => {
      ph.register({
        app: "sideb",
      });
    },
  });
  initialized = true;
}

export function identify(user) {
  if (!initialized || !user?.email) return;
  posthog.identify(user.email, {
    email: user.email,
    name: user.name,
    role: user.role,
  });
}

export function resetIdentity() {
  if (!initialized) return;
  posthog.reset();
}

export function capturePageview(path) {
  if (!initialized) return;
  posthog.capture("$pageview", { path });
}

// ---- Canonical event names — keep this list in sync with backend/server.py ----
export const Events = {
  IntakeSubmitted: "intake_submitted",
  CalendarOpened: "calendar_opened",
  DiscoveryCallBooked: "discovery_call_booked",
  ConsentChanged: "consent_changed",
  // payment_paid is captured server-side from the Stripe webhook (more reliable)
};

export function track(event, properties = {}) {
  if (!initialized) return;
  posthog.capture(event, properties);
}
